import { type FC, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { GridRow, GridCol } from 'dibs-elements/exports/Grid';
import { useGeneratePhotoGrid } from 'dibs-interiors-photos/exports/useGeneratePhotoGrid';
import ProjectPhoto from './ProjectPhoto';
import { useServerVars } from '../shared/ServerVarsContext';

import styles from './ProjectPhotosGrid.scss';

import { type ProjectPhotosGrid_searchResult$key } from './__generated__/ProjectPhotosGrid_searchResult.graphql';

type Props = {
    openModal: (currentUrl: string) => void;
    searchResult: ProjectPhotosGrid_searchResult$key | null | undefined;
};

const COLUMNS_PER_PAGE = 2;

const ProjectPhotosGrid: FC<Props> = props => {
    const { openModal, searchResult: searchResultRef } = props;

    const searchResult = useFragment(
        graphql`
            fragment ProjectPhotosGrid_searchResult on TradeInteriorProjectQueryOutput {
                project {
                    tradePhotos {
                        serviceId
                        ...ProjectPhoto_tradePhoto
                    }
                }
            }
        `,
        searchResultRef
    );

    const { isMobile } = useServerVars();

    const memoizedPhotos = useMemo(() => {
        const photos = searchResult?.project?.tradePhotos || [];
        if (isMobile) {
            return photos;
        }
        return photos.slice(1);
    }, [searchResult, isMobile]);

    const showFullWidthPhoto = isMobile && memoizedPhotos.length === 1;

    const { photoGrid } = useGeneratePhotoGrid({
        columnsPerPage: showFullWidthPhoto ? 1 : COLUMNS_PER_PAGE,
        photoNodes: memoizedPhotos,
    });

    return (
        <div className={styles.wrapper}>
            <GridRow>
                {photoGrid.map((column, columnIndex) => (
                    <GridCol
                        mobile={showFullWidthPhoto ? 12 : 6}
                        key={`photo-column-${columnIndex}`}
                    >
                        {column.map((photo, index) => {
                            const key = photo.serviceId || `${columnIndex}-${index}`;
                            return (
                                <div key={key} className={styles.photoWrapper}>
                                    <ProjectPhoto openModal={openModal} tradePhoto={photo} />
                                </div>
                            );
                        })}
                    </GridCol>
                ))}
            </GridRow>
        </div>
    );
};

export default ProjectPhotosGrid;
